import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { Title } from '@angular/platform-browser';
import { CollectionHelper, ConvertHelper, ObjectHelper } from '@barthauer/abc/core';
import { UserService } from '@barthauer/abc/core/authentication';
import { CacheStorageAdapter } from '@barthauer/abc/core/data';
import { ResourceManager, ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataEntity, MapTarget } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  AutoCompletePanelBehaviour,
  SortOrder,
  StateStorage,
  _AbcGridColumn,
  _AbcGridComponent,
} from '@barthauer/abc/ui-components';
import { ObjectsSetsSettings } from '@barthauer/abc/ui-frameworks/objects-sets';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { ObjectsSetsComponent } from 'libs/abc/ui-frameworks/objects-sets/src/lib/objects-sets.component';
import { lastValueFrom, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BasysMapFrameService, ObjectTypeWithFilter } from '../basys-map-frame.service';
import {
  cellGridButton,
  ComponentDataSource,
  DataSourceColumn,
  DataSourceColumnType,
  DataSourceFilter,
} from '../models/componentDataSource.model';
import { AdditionalObjectTypeConfig } from '../models/config.model';
import { MapFrameSetting } from '../models/map-frame-settings.model';
import { MapFrameSettingsService } from '../services/map-frame-settings.service';
import { ViewModelHelper } from '../shared/helpers';
import { PanelComponent } from '../shared/panel.component';
import { MapInfoObjectTypeViewModel, MapInfoViewModel } from '../viewModels/mapsData.viewModel';
import { ColumnButtonClickEvent } from 'devextreme/ui/data_grid';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class TableComponent extends PanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('abcGridTable', { static: false }) abcGrid: _AbcGridComponent;
  @ViewChild('otSelection', { static: false }) otSelection: MatSelect;
  @ViewChild('searchMenuTrigger', { static: true })
  searchMenuTrigger: MatMenuTrigger;
  @ViewChild('objectsSets', { static: false, read: ViewContainerRef })
  vcr: ViewContainerRef;

  public AutoCompletePanelBehaviour = AutoCompletePanelBehaviour;
  public DataSourceColumnType = DataSourceColumnType;
  public TableStateEnum = TableStateEnum;
  public res = ResourceSet.Empty;
  public mapInfo: MapInfoViewModel;
  static nextObjectType: ObjectTypeWithFilter;
  public get allAutocompleteItems(): string[] {
    if (this.currentDataSource?.data) {
      if (!this.currentDataSource.allAutocompleteItems) {
        const clonedDsData = JSON.parse(JSON.stringify(this.currentDataSource.data));
        clonedDsData.filter((item) => {
          delete item['Id'];
        });
        this.currentDataSource.allAutocompleteItems = ViewModelHelper.flattenAndDistinctToArray(clonedDsData);
      }
    }
    return this.currentDataSource?.allAutocompleteItems ?? [];
  }
  public enableRefresh: boolean = false;
  public enableNavigationToObjects: boolean = false;
  public enableColumnAutoWidth: boolean = true;
  public currentLanguage = ResourceManager.getLanguage();
  public scrollViewPortElementName: string = 'gridContainer';
  public tableState: TableState;
  public get currentDataSource(): ComponentDataSource {
    return this.dataSources[this.currentDataKey];
  }

  public get scrollViewPortHeightVerticalMargin(): number {
    return this.hasGlobalFilters ? 87 : 47;
  }
  public get hasGlobalFilters(): boolean {
    return this.currentDataSource?.filters?.length > 0;
  }
  public get currentObjectSpaceObjectTypes(): MapInfoObjectTypeViewModel[] {
    const filtered: MapInfoObjectTypeViewModel[] = [];
    for (const key in this.dataSources) {
      const ds: ComponentDataSource = this.dataSources[key];
      if (
        ds?.ot?.objectType &&
        !filtered.includes(ds.ot.objectType) &&
        ds.filters?.find((_) => _.key === DataSourceFilter.MultiSelectionKey) &&
        ds.data?.length &&
        this.mapInfo.objectSpaceObjectTypes.includes(ds.ot.objectType)
      ) {
        filtered.push(ds.ot.objectType);
      }
    }
    if (filtered.length) {
      return filtered;
    }
    return this.mapInfo.objectSpaceObjectTypes;
  }
  public get currentDataSets(): MapInfoObjectTypeViewModel[] {
    const filtered: MapInfoObjectTypeViewModel[] = [];
    for (const key in this.dataSources) {
      const ds: ComponentDataSource = this.dataSources[key];
      if (
        ds?.ot?.objectType &&
        !filtered.includes(ds.ot.objectType) &&
        ds.filters?.find((_) => _.key === DataSourceFilter.MultiSelectionKey) &&
        ds.data?.length &&
        this.mapInfo.dataSets.includes(ds.ot.objectType)
      ) {
        filtered.push(ds.ot.objectType);
      }
    }
    if (filtered.length) {
      return filtered;
    }
    return this.mapInfo.dataSets;
  }

  private readonly loadMapInfoObjectTypeSubject = new Subject<ObjectTypeWithFilter>();
  private currentObjectType: MapInfoObjectTypeViewModel;
  private stateStorageKey: string;
  private currentProfileName: string;
  private currentUserInfo: string;
  private currentFilter: string;
  private currentDataKey: string;
  private dataSources: { [key: string]: ComponentDataSource };
  private scroll: any;
  private scrollPosition: number;

  constructor(
    public frame: BasysMapFrameService,
    private resourceService: ResourceManagerService,
    private userService: UserService,
    private titleService: Title,
    private settingsService: MapFrameSettingsService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    super.init(frame);
    this.dataSources = {};
    const storage = this.settingsService.createStorage(MapFrameSetting.gridLayouts);
    this.mapInfo = frame.mapInfo;

    let lastDataSet: ComponentDataSource;
    CacheStorageAdapter.isOfflineChanged.subscribe(() => {
      this.updateObjectSets(lastDataSet);
    });

    this.loadMapInfoObjectTypeSubject
      .pipe(
        switchMap((ot) => {
          this.publishState(TableStateEnum.Loading);
          this.cdr.markForCheck();
          return this.queryDataSource(ot);
        }),
      )
      .subscribe({
        next: async (ds) => {
          lastDataSet = ds;

          this.updateObjectSets(ds);
          setTimeout(async () => {
            try {
              const mappedColumns =
                ds.columns?.map((_) => {
                  const c = new _AbcGridColumn();
                  ObjectHelper.mergeDeepInto(c, <_AbcGridColumn>_);
                  return c;
                }) || [];

              const subscription = this.abcGrid.onDataSourceSet.subscribe(() => {
                subscription?.unsubscribe();
                this.publishState(TableStateEnum.ObjectTypeLoaded);

                this.abcGrid.instance?.repaint();
                this.cdr.markForCheck();
              });

              await this.abcGrid.setDataSourceEx(ds.data, async () => mappedColumns, ds.storageKey);
            } catch (err) {
              console.error('Error applying grid datasource:', err);
              this.publishState(TableStateEnum.Error, this.resolveError(err));
            }
          }, 0);
        },
        error: (err) => {
          this.publishState(TableStateEnum.Error, this.resolveError(err));
        },
      });

    this.frame
      .getCurrentProfile()
      .pipe(take(1))
      .subscribe((profile) => {
        this.currentProfileName = profile.id;
        if (TableComponent.nextObjectType && this.currentProfileName) {
          this.setObjectTypeValue(TableComponent.nextObjectType);
        }
      });
  }

  private updateObjectSets(ds: ComponentDataSource) {
    if (ds?.additionalGridContextMenu.objectsSetsInstance && !CacheStorageAdapter.useOfflineMode) {
      this.abcGrid.objectsSetsInstance = () => ds.additionalGridContextMenu.objectsSetsInstance();
      this.abcGrid.keyExpr = 'Id';
      this.abcGrid.focusedRowEnabled = true;
    } else {
      this.abcGrid.objectsSetsInstance = undefined;
      this.abcGrid.keyExpr = undefined;
      this.abcGrid.focusedRowEnabled = false;
    }
  }

  async ngOnInit() {
    super.ngOnInit();
    this.res = await this.resourceService.getResourceSet('basys-map-frame');
    this.frame.lib.reloadGrid.subscribe((e) => {
      if (this.abcGrid) {
        this.scroll?.scrollTo(this.scrollPosition);
        if (this.abcGrid.instance.refresh) {
          this.abcGrid.instance.refresh();
        }
      }
    });
    this.subscriptions.push(
      this.userService.getCurrentLoginInfo().subscribe((userInfo) => {
        this.currentUserInfo = userInfo.displayName;
      }),
    );
    this.subscriptions.push(
      this.frame.bottomSheetClosing$.subscribe(() => {
        this.otSelection?.close();
        this.abcGrid?.instance?.hideColumnChooser();
      }),
    );
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.publishState(TableStateEnum.NoObjectType);
    }, 100);
    this.frame.onOpenDataTable().subscribe(
      function (ot: ObjectTypeWithFilter) {
        this.setObjectTypeValue(ot);
      }.bind(this),
    );
    this.frame.onOpenDataTableSelection().subscribe(async (entities) => {
      this.publishState(TableStateEnum.Loading);
      this.removeMultiSelectFilter();
      const candidates: MapInfoObjectTypeViewModel[] = [];
      for (const ot of CollectionHelper.flatten([this.mapInfo.objectSpaceObjectTypes, this.mapInfo.dataSets])) {
        for (const g of CollectionHelper.groupBy(entities, (_) => _.objectType)) {
          const dataObjectType = await lastValueFrom(this.frame.objectSpace.getObjectType(g.key));
          if (ot.objectType && dataObjectType.isMatchOrBase(ot.objectType.name)) {
            candidates.push(ot);
            const ds = await lastValueFrom(
              this.queryDataSource({
                objectType: ot,
                filter: '',
              }),
            );
            if (!this.otSelection.value) {
              this.otSelection.value = ds?.ot?.objectType;
              this.selectionChange(ds.ot?.objectType, ds.ot?.filter);
            }
          }
        }
      }
      this.publishState(TableStateEnum.Loading);
      let found: boolean = false;
      for (const key in this.dataSources) {
        const ds: ComponentDataSource = this.dataSources[key];
        if (candidates.includes(ds?.ot?.objectType)) {
          const dsFilter: DataSourceFilter = new DataSourceFilter();
          ((dsFilter.displayText = (await this.frame.lib.resAsync).get('Table-SelectionFromMap')),
            (dsFilter.key = DataSourceFilter.MultiSelectionKey));
          dsFilter.objectInfos = entities;
          ds.addFilter(dsFilter);
          if (ds?.data?.length && this.otSelection?.value === ds.ot.objectType) {
            this.selectionChange(ds.ot?.objectType, ds.ot?.filter);
            found = true;
          }
        }
      }
      for (const key in this.dataSources) {
        const ds: ComponentDataSource = this.dataSources[key];
        if (
          candidates.includes(ds?.ot?.objectType) &&
          ds?.data?.length &&
          ds.filters.find((_) => _.key === DataSourceFilter.MultiSelectionKey) &&
          !found
        ) {
          this.otSelection.value = ds.ot?.objectType;
          this.selectionChange(ds.ot?.objectType, ds.ot?.filter);
          found = true;
        }
      }
      if (!found) {
        this.frame.map.sendMapShortMessage((await this.frame.lib.resAsync).get('NoObjectFoundError'));
        this.removeMultiSelectFilter();
      }
      this.updateSize();
      this.cdr.detectChanges();
    });

    if (TableComponent.nextObjectType && this.currentProfileName) {
      this.setObjectTypeValue(TableComponent.nextObjectType);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private setObjectTypeValue(ot: ObjectTypeWithFilter, retry: number = 0) {
    try {
      this.otSelection.value = ot.objectType;
      this.selectionChange(ot.objectType, ot.filter, ot.storageKey);
      TableComponent.nextObjectType = null;
    } catch (e) {
      if (retry < 5) {
        setTimeout(
          function () {
            this.setObjectTypeValue(ot, retry + 1);
          }.bind(this),
          100,
        );
      } else {
        this.publishState(TableStateEnum.Error, this.resolveError(e));
      }
    }
  }

  public static setNextObjectTypeValue(ot: ObjectTypeWithFilter) {
    TableComponent.nextObjectType = ot;
  }

  private resolveError(error: any): string {
    let msg: string = 'Unbekannt';
    if (error) {
      if (error.statusText) {
        msg = error.statusText;
      }
      if (error.message) {
        msg = error.message;
      }
      if (error.error && error.error.Message) {
        msg = error.error.Message;
      }
    }
    return msg;
  }

  private publishState(state: TableStateEnum, message?: string) {
    this.tableState = new TableState({
      state: state,
      message: message,
    });
  }

  private queryDataSource(ot: ObjectTypeWithFilter): Observable<ComponentDataSource> {
    let key = ot.objectType.objectSetName ?? ot.objectType.name;
    if (ot.filter) {
      key += '|' + ot.filter;
    }
    if (this.dataSources[key] && !ot.refresh) {
      this.currentDataKey = key;
      return of(this.currentDataSource);
    } else {
      return this.frame.getMapObjects(ot.objectType, ot.filter).pipe(
        map((obj) => {
          const ds = new ComponentDataSource();
          if (!Array.isArray(obj)) {
            obj = [];
          }
          ds.data = obj;
          ds.storageKey = ot.storageKey;
          ds.ot = ot;
          let hasId: boolean = obj.length && obj[0][this.frame.IdColumnName];
          let hasObjectType: boolean = obj.length && obj[0][this.frame.ObjectTypeColumnName];
          for (const column of ot.objectType.columns) {
            if (column.name === this.frame.IdColumnName) {
              hasId = true;
            } else if (column.name === this.frame.ObjectTypeColumnName) {
              hasObjectType = true;
            } else {
              const dsCol = new DataSourceColumn();
              dsCol.dataField = column.name;
              dsCol.name = column.name;
              dsCol.caption = column.displayName;
              dsCol.sortOrder = SortOrder[column.sortOrder];
              dsCol.dataType = column.dataType;
              dsCol.format = _AbcGridComponent.getColumnFormat(column.dataType, column.editMask);
              dsCol.allowGrouping = true;
              dsCol.allowFiltering = true;
              ds.addColumn(dsCol);
            }
          }

          if (
            hasId &&
            hasObjectType &&
            ot.objectType.objectType &&
            !ot.objectType.dataSourceId &&
            ObjectsSetsSettings.getCurrentContainerId
          ) {
            const dsIdColumn = new DataSourceColumn();
            dsIdColumn.dataField = this.frame.IdColumnName;
            dsIdColumn.name = this.frame.IdColumnName;
            dsIdColumn.caption = this.frame.IdColumnName;
            dsIdColumn.showInColumnChooser = false;
            dsIdColumn.allowFiltering = true;
            dsIdColumn.visible = false;
            ds.addColumn(dsIdColumn);
            ds.additionalGridContextMenu.objectsSetsInstance = () => this.showObjectsSets();
          }

          ds.addColumn(
            this.createActionColumn(
              'actSelectInMap',
              this.res.get('Table-NavigateTo'),
              this.res.svgUrl('Locate-Image'),
              (row) => this.selectObjectInMap(row),
            ),
          );
          if (ot.objectType.objectType && !ot.objectType.dataSourceId) {
            if (
              ot.objectType.commands.find((_) => _.name === BasysMapFrameService.ContextMenuCommandName_Map_EditObject)
            ) {
              ds.addColumn(
                this.createActionColumn(
                  'actShowObjectInfo',
                  this.res.get('Table-ObjectInfo'),
                  this.res.svgUrl('ObjectInfoOpen-Image'),
                  (row: ColumnButtonClickEvent) => {
                    this.showObjectInfo(row);
                  },
                ),
              );
            }
            if (
              ot.objectType.commands.find(
                (_) => _.name === BasysMapFrameService.ContextMenuCommandName_Map_ShowDocumentsList,
              )
            ) {
              ds.addColumn(
                this.createActionColumn(
                  'actShowDocuments',
                  'Dokumente',
                  this.res.svgUrl('DocumentsOpen-Image'),
                  (row) => this.showDocuments(row),
                ),
              );
            }
          } else if (
            !ot.objectType.objectType &&
            ot.objectType.objectSetName &&
            this.frame.map.targetButtonNames?.find(
              (_) => _ === BasysMapFrameService.ContextMenuCommandName_Map_OpenTargetUrl,
            )
          ) {
            const t = this.frame.map.getTargetOfObjectSet(ot.objectType.objectSetName);
            if (t) {
              ds.addColumn(
                this.createActionColumn(
                  'actOpenTargetUrl',
                  this.res.get('Table-OpenTargetUrl'),
                  this.res.svgUrl('TargetUrlOpen-Image'),
                  (row) => this.openLayerTargetUrl(row, t),
                ),
              );
            }
          }
          this.dataSources[key] = ds;
          this.currentDataKey = key;
          return this.currentDataSource;
        }),
      );
    }
  }

  private createActionColumn(
    name: string,
    displayName: string,
    svgIconName: string,
    method = (row: any) => {},
  ): DataSourceColumn {
    const newActionCol = new DataSourceColumn();
    newActionCol.dataField = '';
    newActionCol.name = name;
    newActionCol.type = 'buttons';
    newActionCol.width = '35px';
    newActionCol.allowResizing = false;
    (<any>newActionCol).cssClass = 'tableActionColumn dx-command-edit dx-command-edit-with-icons';

    const cellGridButtons: cellGridButton = new cellGridButton();
    cellGridButtons.icon = svgIconName;
    cellGridButtons.onClick = method;
    cellGridButtons.hint = displayName;

    newActionCol.buttons = [cellGridButtons];
    return newActionCol;
  }

  public selectionChange(
    objectType: MapInfoObjectTypeViewModel,
    filter: string = null,
    stateStorageKey: string = null,
  ) {
    this.currentFilter = filter;
    this.currentObjectType = objectType;
    this.enableRefresh = true;
    const values = this.titleService.getTitle().split(' ');
    const projectTitle = values[1];

    const currentName =
      stateStorageKey ?? (objectType.objectSetName ? objectType.objectSetName + '_' : '') + objectType.displayText;

    this.stateStorageKey = ConvertHelper.stringReplaceSpecialCharacters(currentName, ' ', '_', true);

    const objectInfoStorageKey =
      ConvertHelper.stringReplaceSpecialCharacters(this.currentUserInfo, ' ', '_', true) +
      '_' +
      ConvertHelper.stringReplaceSpecialCharacters(this.currentProfileName, ' ', '_', true) +
      StateStorage.objectInfo +
      ConvertHelper.stringReplaceSpecialCharacters(currentName, ' ', '_', true);

    this.frame.lib.mapInfoStateStorageKey.next(objectInfoStorageKey);
    this.loadMapInfoObjectTypeSubject.next({
      objectType: objectType,
      filter: filter,
      storageKey: this.stateStorageKey,
    });
  }

  private applyFilterObject(dsFilter: DataSourceFilter) {
    if (this.currentDataSource) {
      this.currentDataSource.addFilter(dsFilter);
      this.abcGrid.instance.option('dataSource', this.currentDataSource.data);
      super.updateSize();
    }
  }

  private selectObjectInMap(row: any): void {
    const data: any = row.row.data;
    if (this.otSelection.value?.objectType) {
      const addConfig: AdditionalObjectTypeConfig = this.frame.getAdditionalObjectTypeConfig(
        this.otSelection.value?.objectType?.name,
      );

      let entity: any;
      if (addConfig && addConfig.navigateObjectIdColumnName && addConfig.navigateObjectTypeColumnName) {
        entity = {
          id: data[addConfig.navigateObjectIdColumnName],
          objectType: data[addConfig.navigateObjectTypeColumnName],
        };
      } else {
        entity = {
          id: data.Id ?? data.id ?? data.ID,
          objectType: data.ObjectType ?? data.objectType ?? data.OBJECTTYPE,
        };
      }

      this.frame.map.navigateToObjects([entity]);
    } else {
      const entity = this.getLayerEntity(row);
      this.frame.map.navigateToDataRows([
        {
          DataSetName: this.otSelection.value.name,
          Id: entity?.id,
        },
      ]);
    }
    this.frame.lib.closeBottomSheet();
  }

  private getLayerEntity(row: any) {
    const data: any = row.row.data;

    let id: string;
    if (this.otSelection.value.identityColumnName) {
      id = data[this.otSelection.value.identityColumnName];
    }
    if (!id) {
      id = data._rowid ?? Object.values(row)[0];
    }
    if (id) {
      return new DataEntity(this.otSelection.value.objectSetName, id);
    }
  }

  private showObjectInfo(row: ColumnButtonClickEvent): void {
    const data: any = row.row.data;
    this.frame.lib.showObjectInfo([
      {
        id: data.Id ?? data.id ?? data.ID,
        objectType: data.ObjectType ?? data.objectType ?? data.OBJECTTYPE,
      },
    ]);
  }

  private showDocuments(row: any): void {
    const data: any = row.row.data;
    this.frame.lib.showDocuments(
      [
        {
          id: data.Id ?? data.id ?? data.ID,
          objectType: data.ObjectType ?? data.objectType ?? data.OBJECTTYPE,
        },
      ],
      true,
    );
  }

  private openLayerTargetUrl(row: any, target: MapTarget): void {
    const entity = this.getLayerEntity(row);
    this.frame.lib.openTargetUrl(
      [
        {
          id: entity?.id,
          objectType: this.otSelection.value.objectSetName,
        },
      ],
      target,
      (e, n) => Promise.resolve(row?.row?.data ? row?.row?.data[n] : null),
    );
  }

  public async selectObjectsInMap(): Promise<void> {
    const filteredObjects = await this.abcGrid.getFilteredObjects();

    const entities: any = [];
    if (filteredObjects?.length) {
      {
        for (const object of filteredObjects) {
          const addtionalEntity = this.getAdditionalEntity(object);
          if (!!addtionalEntity) {
            entities.push(addtionalEntity);
          } else {
            entities.push({
              id: object.Id ?? object.id ?? object.ID,
              objectType: object.ObjectType ?? object.objectType ?? object.OBJECTTYPE,
            });
          }
        }
      }
      this.frame.map.navigateToObjects(entities);
      this.frame.lib.closeBottomSheet();
    }
  }

  private getAdditionalEntity(object: any) {
    let entity = undefined;
    if (!!object) {
      if (this.otSelection.value?.objectType) {
        const addConfig: AdditionalObjectTypeConfig = this.frame.getAdditionalObjectTypeConfig(
          this.otSelection.value?.objectType?.name,
        );

        if (addConfig && addConfig.navigateObjectIdColumnName && addConfig.navigateObjectTypeColumnName) {
          const id = object[addConfig.navigateObjectIdColumnName];
          const objectType = object[addConfig.navigateObjectTypeColumnName];
          if (!!id && !!objectType) {
            entity = {
              id: id,
              objectType: objectType,
            };
          }
        }
      }
    }

    return entity;
  }

  public toggleColumnAutoWidth(): void {
    this.enableColumnAutoWidth = !this.enableColumnAutoWidth;
  }

  public removeFilter(filter: DataSourceFilter): void {
    if (filter?.key === DataSourceFilter.MultiSelectionKey) {
      this.removeMultiSelectFilter();
    } else {
      this.currentDataSource.removeFilter(filter);
      this.abcGrid.instance.option('dataSource', this.currentDataSource.data);
    }
    super.updateSize();
  }

  private removeMultiSelectFilter(): void {
    for (const key in this.dataSources) {
      const ds: ComponentDataSource = this.dataSources[key];
      const multi = ds.filters?.find((_) => _.key === DataSourceFilter.MultiSelectionKey);
      if (multi) {
        ds.removeFilter(multi);
        if (ds === this.currentDataSource) {
          this.abcGrid.instance.option('dataSource', ds.data);
        }
      }
    }
  }

  public applyFilter(filter: string): void {
    setTimeout(() => this.searchMenuTrigger.closeMenu(), 0);
    const dsFilter: DataSourceFilter = new DataSourceFilter();
    dsFilter.value = filter;
    this.applyFilterObject(dsFilter);
  }

  public onCellPrepared(e): void {
    if (e.data && e.column?.name === 'actShowDocuments' && e.cellElement) {
      const img: HTMLImageElement = e.cellElement.querySelector('img');
      if (e.data.ExistsDocuments === 0) {
        e.cellElement.style.pointerEvents = 'none';
        if (img) {
          img.src = this.res.svgUrl('DocumentsNotFound-Image');
        }
      } else if (img?.parentElement) {
        const span = document.createElement('span');
        span.setAttribute('class', 'badgeDocument');
        span.appendChild(document.createTextNode(e.data.ExistsDocuments));
        img.parentElement.appendChild(span);
      }
    }
  }

  public refreshTable(): void {
    if (this.currentObjectType) {
      this.loadMapInfoObjectTypeSubject.next({
        objectType: this.currentObjectType,
        filter: this.currentFilter,
        storageKey: this.stateStorageKey,
        refresh: true,
      });
    }
  }

  private async showObjectsSets() {
    const coreDataEntities = this.abcGrid.instance.getDataSource()?.items();

    if (coreDataEntities?.length) {
      const s = this.vcr.createComponent(ObjectsSetsComponent);
      const osc: ObjectsSetsComponent = <ObjectsSetsComponent>s?.instance;

      if (osc) {
        const objectType = coreDataEntities[0]?.ObjectType;
        osc.parentObjectType = objectType;

        osc.sourceDataEntities = <ICoreDataEntity>() => {
          return {
            objectType: objectType,
            entities: coreDataEntities as ICoreDataEntity[],
            idExpression: (id) => this.frame.IdColumnName,
            objectTypeExpression: (ot) => this.frame.ObjectTypeColumnName,
          };
        };
        osc.loadObjectsSetsWindow();
        osc.onSelectEntities = async (object) => {
          const entities = object?.entities;
          const ids = entities.map((entity) => entity.Id);
          this.abcGrid.filterByRowKeys(ids);
        };
      }
    }
  }
}

export enum TableStateEnum {
  NoObjectType = 1,
  Loading,
  ObjectTypeLoaded,
  Error,
}

export class TableState {
  message: string;
  state: TableStateEnum;

  constructor(initObj?: any) {
    Object.assign(this, initObj);
  }
}
